import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getDatabaseFieldTypeOptions } from '@/constants/databaseFieldTypes'
import type { DatabaseDialect } from '@/types'
import { MorphSelect, type MorphSelectOption } from '@/components/ui/MorphSelect'

function normalizeColumnType(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return ''
  const upper = trimmed.toUpperCase()
  if (upper === 'BOOL' || upper === 'BOOLEAN') return 'BOOLEAN'
  return trimmed
}

function splitTypeParameters(value: string): { base: string; parameters: string } | null {
  const match = value.trim().match(/^(.+?)\s*\(([^()]*)\)$/)
  if (!match) return null
  return { base: match[1].trim(), parameters: match[2] }
}

function sanitizeTypeParameters(base: string, value: string): string {
  const normalizedBase = base.trim().toUpperCase()
  const supportsPrecisionScale = ['DECIMAL', 'NUMERIC', 'NUMBER'].includes(normalizedBase)

  if (!supportsPrecisionScale) {
    const normalizedValue = value.trim().toUpperCase()
    if (['NVARCHAR', 'VARBINARY'].includes(normalizedBase) && normalizedValue === 'MAX') return 'MAX'
    return value.replace(/\D/g, '')
  }

  let hasComma = false
  return Array.from(value).filter((character) => {
    if (/\d/.test(character)) return true
    if (character === ',' && !hasComma) {
      hasComma = true
      return true
    }
    return false
  }).join('')
}

interface ColumnTypeInputProps {
  value: string
  onChange: (value: string) => void
  onCommit?: (value: string) => void
  placeholder?: string
  dialect?: DatabaseDialect
}

export function ColumnTypeInput({
  value,
  onChange,
  onCommit,
  placeholder,
  dialect,
}: ColumnTypeInputProps) {
  const { t } = useTranslation()
  const parameterizedType = splitTypeParameters(value)
  const options = useMemo(() => {
    const baseOptions = getDatabaseFieldTypeOptions(dialect)
    const normalizedValue = normalizeColumnType(value)

    if (!normalizedValue || baseOptions.some((option) => option.value === normalizedValue)) {
      return baseOptions
    }

    const currentValueOption: MorphSelectOption = {
      label: normalizedValue,
      value: normalizedValue,
      metadata: { aliases: [normalizedValue.toLowerCase()] },
    }

    return [currentValueOption, ...baseOptions]
  }, [dialect, value])

  return (
    <div className={parameterizedType ? 'grid min-w-0 grid-cols-[minmax(0,1fr)_64px] gap-1' : undefined}>
      <MorphSelect
        ariaLabel={t('columnTypeInput.searchPlaceholder')}
        value={value || null}
        options={options}
        placeholder={placeholder ?? t('columnTypeInput.placeholder')}
        searchable
        searchPlaceholder={t('columnTypeInput.searchPlaceholder')}
        emptyMessage={t('columnTypeInput.emptyMessage')}
        onChange={(nextValue) => {
          const normalized = normalizeColumnType(nextValue ?? '')
          onChange(normalized)
          onCommit?.(normalized)
        }}
      />
      {parameterizedType ? (
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9,]*"
          value={parameterizedType.parameters}
          aria-label={t('columnTypeInput.parameters', 'Parâmetros do tipo')}
          title={`${parameterizedType.base}(…)`}
          className="morph-input min-w-0 px-1.5 text-center text-[10px]"
          onChange={(event) => {
            const parameters = sanitizeTypeParameters(parameterizedType.base, event.target.value)
            onChange(`${parameterizedType.base}(${parameters})`)
          }}
          onBlur={() => onCommit?.(normalizeColumnType(value))}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              onCommit?.(normalizeColumnType(value))
              event.currentTarget.blur()
            }
          }}
        />
      ) : null}
    </div>
  )
}

export { normalizeColumnType, sanitizeTypeParameters, splitTypeParameters }
